import * as React from "react";
import clsx from "clsx";
import { useVendorPrices } from "@/lib/useVendorPrices";
import { useSpellgatherAvg } from "@/lib/useSpellgatherAvg";

interface PriceOverlayProps {
  scryfallId: string;
  compact?: boolean;
  className?: string;
  size?: "xs" | "sm" | "md";
  tone?: "emerald" | "amber" | "cyan";
}

const sizeMap = {
  xs: "text-[10px] px-1.5 py-[2px]",
  sm: "text-[12px] px-2 py-[3px]",
  md: "text-sm px-2.5 py-1",
};

const toneMap = {
  emerald: "text-emerald-300",
  amber: "text-amber-200",
  cyan: "text-cyan-200",
  white: "text-white",
};

const money = (n: number | null | undefined) =>
  typeof n === "number" && isFinite(n) ? `$${n.toFixed(2)}` : "—";

export function PriceOverlay({
  scryfallId,
  compact,
  className,
  size = "xs",
  tone = "emerald",
}: PriceOverlayProps) {
  if (!scryfallId) return null;

  const {
    tcg,
    cardkingdom,
    averageMarket,
    updatedAt,
    isLoading,
    error,
    endpointConfigured,
  } = useVendorPrices(scryfallId);
  const { avg: sgAvg } = useSpellgatherAvg(scryfallId);

  const updatedStr = updatedAt
    ? (() => {
        try {
          return new Date(updatedAt).toLocaleString();
        } catch {
          return updatedAt;
        }
      })()
    : null;

  // Row
  const Row: React.FC<{ label: string; values: string[] }> = ({
    label,
    values,
  }) => (
    <div
      className="flex items-center justify-between gap-2 leading-tight"
      style={{ fontSize: "inherit", lineHeight: 1.05 }}
    >
      <span className="font-semibold">{label}</span>
      <span className="tabular-nums text-right flex-1 ml-2">
        {values.join("  ·  ")}
      </span>
    </div>
  );

  // CONTENT
  const ContentBlock = () => {
    if (!endpointConfigured) {
      return (
        <div
          className="opacity-70"
          style={{ fontSize: "inherit" }}
        >
          Pricing unavailable
        </div>
      );
    }
    if (isLoading && !error) {
      return <div className="h-full w-full rounded-md bg-white/10 animate-pulse" />;
    }
    if (error) {
      return (
        <div
          className="text-red-300 font-semibold"
          style={{ fontSize: "inherit" }}
        >
          Pricing error
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <Row label="TCG Player" values={[`Market ${money(tcg?.market)}`]} />
        <Row label="Card Kingdom" values={[`Market ${money(cardkingdom?.market)}`]} />
        <Row label="Avg Market" values={[money(averageMarket)]} />
        <Row label="Spellgather" values={[money(sgAvg)]} />
        <div
          className="opacity-60 tabular-nums mt-0.5"
          style={{ fontSize: "inherit", lineHeight: 1.05 }}
        >
          {updatedStr ? `Updated ${updatedStr}` : "Updated —"}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`
        price-overlay-root inline-flex items-center gap-1 rounded-md border border-white/10
        bg-black/55 backdrop-blur-md font-medium leading-none
        ${sizeMap[size]} ${toneMap[tone]}
        ${className || ""}
      `}
    >
      <ContentBlock />
    </div>
  );
}
